import { Point } from "geojson";

import { DeviceBase, DeviceBaseV2, deviceState, DeviceStates } from "types";

import { hwTypeToDeviceType } from "../deviceHwType";
import { convertToISO } from "../formatDate/formatDate";

const convertToISODefined = (date: string | undefined) => (date ? convertToISO(date) : undefined);

const getDeviceState = (asset: DeviceBaseV2): DeviceStates => {
  if (asset.config.is_marked_lost) {
    return [deviceState.Lost];
  }
  return [deviceState.Found];
};

const getDeviceMeta = (asset: DeviceBaseV2) => {
  return {
    firmware: asset.config.fw_version || asset.specification?.fw_version,
    modelNum: asset.config.model_name || asset.specification?.model_name,
    secured: asset.specification?.secured || asset.network?.ble?.secure_state === "secured" ? 1 : 0,
    lowBatt: asset.power?.is_battery_low || asset.specification?.lowBatt
  };
};

export const websocketDeviceToDevice = (notification: DeviceBaseV2): Partial<DeviceBase> => {
  const { tracking, network } = notification;
  const deviceType = hwTypeToDeviceType(notification.specification?.hw_type);
  let location: null | Point = null;
  if (tracking?.location_estimate) {
    location = {
      type: "Point",
      coordinates: [tracking.location_estimate.lon, tracking.location_estimate.lat]
    };
  }

  return {
    // COMMON
    id: notification.id,
    ...(deviceType && { type: deviceType }),
    name: notification.config.asset_name,
    created_at: convertToISODefined(notification.config.create_time),
    updated_at: convertToISODefined(notification.update_time),
    features: notification.specification?.is_bidirectional ? ["lamp"] : [],
    state: getDeviceState(notification),
    space_id: `${notification.config.organization_id}.${notification.config.workspace_id}`,
    space_name: notification.config.workspace_name,
    meta: getDeviceMeta(notification),
    ...(notification.config.labels != null && { tags: notification.config.labels }),
    ...(notification.config.created_by != null && {
      created_by: notification.config.created_by
    }),
    ...(notification.config.updated_by != null && {
      updated_by: notification.config.updated_by
    }),

    // GATEWAY
    best_gwid_by_effective_rssi: tracking?.methods?.ble_gateway_anchor?.anchor_name,
    gwid_effective_rssi: tracking?.methods?.ble_gateway_anchor?.ble_rssi,
    best_notification_time_by_effective_rssi: convertToISODefined(
      tracking?.methods?.ble_gateway_anchor?.update_time
    ),

    // GEOLOCATION
    ...(location && { location }),
    ...(tracking?.location_estimate?.address && { address: tracking?.location_estimate?.address }),
    site_name: tracking?.location_estimate?.sites[0]?.site_name,
    zone_name: tracking?.location_estimate?.sites[0]?.zone?.zone_name,
    located_at: convertToISODefined(tracking?.location_estimate?.update_time),
    tracking: notification.tracking,

    // PRESENCE
    is_present: network?.is_connected,
    present_at: convertToISODefined(network?.last_notification?.device_send_time ?? undefined),
    gw_name: network?.ble?.last_notification?.gateway_name,

    // WIFI
    wifi_name: tracking?.methods?.wifi_ap_anchor_via_ble_gateway?.anchor_name,
    wifi_ssid: tracking?.methods?.wifi_ap_anchor_via_ble_gateway?.ssid,
    wifi_bssid: tracking?.methods?.wifi_ap_anchor_via_ble_gateway?.bssid,
    wifi_present_at: convertToISODefined(
      tracking?.methods?.wifi_ap_anchor_via_ble_gateway?.update_time
    ),

    // V2 PASS-THROUGH (for Device config, Device specs & Device state column groups)
    config: notification.config,
    specification: notification.specification,
    network: notification.network,
    power: notification.power
  };
};
